/**
 * screens/admin/adminPlans.tsx — Plans CRUD.
 * Faithful RN port of design/admin.jsx PlansPage + PlanEditor: table with
 * bundled-app icon stack, inline publish/unpublish, and a drawer editor with
 * app multi-select + limits grid + visibility/popular. Admin Plan-authoring
 * routes are a flagged 00-vision gap (see api-endpoints); operates on the copy.
 */

import React, { useEffect, useState } from 'react';
import { View, Text, ScrollView, Pressable } from 'react-native';

import { useTheme } from '../../context/themeContext';
import { usePlatform } from '../../context/platformContext';
import { useToast } from '../../components/toast';
import { SettingsHeader, Panel, DataTable, Tag, StatusBadge, SideDrawer } from '../../components/platform/primitives';
import { PButton, PTextInput, PSelect, PSwitch } from '../../components/platform/controls';
import Icon from '../../components/icon';
import { AppIcon, ConfirmModal } from './adminShared';
import { PermissionDenied } from '../shell/chrome';
import { FS, FW, R, inr } from '../../utils/platformTokens';

type Plan = typeof import('../../data/platformSeed').SEED.adminPlans[number];

const LIMIT_FIELDS = [
  { key: 'contacts', label: 'Active contacts' },
  { key: 'seats', label: 'Team seats' },
  { key: 'fields', label: 'Custom fields' },
  { key: 'voiceMin', label: 'Suki Voice minutes' },
  { key: 'auditDays', label: 'Audit retention' },
] as const;

const PlanEditor: React.FC<{ open: boolean; onClose: () => void; plan: Plan | null }> = ({ open, onClose, plan }) => {
  const { theme } = useTheme();
  const c = theme.colors;
  const { state, setState } = usePlatform();
  const toast = useToast();
  const isNew = !plan;
  const blank: any = { name: '', price: 0, cycle: 'monthly', status: 'draft', visibility: 'public', popular: false, apps: [], limits: { contacts: 250, seats: 3, fields: 5, voiceMin: 0, auditDays: 30 } };
  const [form, setForm] = useState<any>(blank);
  const [err, setErr] = useState<{ name?: string; apps?: string }>({});
  useEffect(() => { if (open) { setForm(plan ? JSON.parse(JSON.stringify(plan)) : blank); setErr({}); } }, [open, plan]);
  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));
  const setLimit = (k: string, v: number) => setForm((f: any) => ({ ...f, limits: { ...f.limits, [k]: v } }));
  const toggleApp = (id: string) => setForm((f: any) => ({ ...f, apps: f.apps.includes(id) ? f.apps.filter((x: string) => x !== id) : [...f.apps, id] }));

  const commit = (status: string) => {
    const e: typeof err = {};
    if (!form.name.trim()) e.name = 'Plan name is required.';
    if (form.apps.length === 0) e.apps = 'Select at least one application.';
    setErr(e);
    if (Object.keys(e).length) return;
    const next = { ...form, status };
    setState((s) => ({
      ...s,
      adminPlans: isNew ? [...s.adminPlans, { ...next, id: 'pl_' + form.name.toLowerCase().replace(/[^a-z0-9]+/g, '_') }] : s.adminPlans.map((p) => (p.id === plan!.id ? { ...next } : p)),
    }));
    toast.success(isNew ? `${form.name} plan created.` : `${form.name} plan updated.`);
    onClose();
  };

  return (
    <SideDrawer open={open} onClose={onClose} title={isNew ? 'Create plan' : `Edit ${plan?.name} plan`}
      footer={<>
        <PButton kind="ghost" onPress={onClose}>Cancel</PButton>
        <View style={{ flexDirection: 'row', gap: 8 }}>
          <PButton onPress={() => commit('draft')}>Save as draft</PButton>
          <PButton kind="accent" onPress={() => commit('published')}>{isNew ? 'Create & publish' : 'Save & publish'}</PButton>
        </View>
      </>}>
      <View style={{ gap: 18 }}>
        <PTextInput label="Plan name" placeholder="Professional" value={form.name} onChangeText={(v) => set('name', v)} error={err.name} />
        <View style={{ flexDirection: 'row', gap: 12 }}>
          <View style={{ flex: 1 }}>
            <PTextInput label="Price (₹)" mono keyboardType="numeric" value={String(form.price)} onChangeText={(v) => set('price', +v || 0)} />
          </View>
          <View style={{ flex: 1 }}>
            <PSelect label="Billing cycle" value={form.cycle} onChange={(v) => set('cycle', v)} options={[{ value: 'monthly', label: 'Monthly' }, { value: 'annual', label: 'Annual' }]} />
          </View>
        </View>

        <View>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 8 }}>
            <Text style={{ fontSize: FS.sm, fontWeight: FW.medium, color: c.ink2 }}>Included applications</Text>
            <Text style={{ fontSize: FS.xs, color: c.ink3 }}>{form.apps.length} selected</Text>
          </View>
          <View style={{ gap: 6, borderWidth: 1, borderColor: err.apps ? c.danger : c.line, borderRadius: R.r3, padding: 8 }}>
            {state.applications.map((a) => {
              const on = form.apps.includes(a.id);
              return (
                <Pressable key={a.id} onPress={() => toggleApp(a.id)}
                  style={{ flexDirection: 'row', alignItems: 'center', gap: 10, padding: 8, borderRadius: R.r2, backgroundColor: on ? c.accentSoft : 'transparent' }}>
                  <View style={{ width: 18, height: 18, borderRadius: 5, alignItems: 'center', justifyContent: 'center', backgroundColor: on ? c.accent : c.card, borderWidth: on ? 0 : 1, borderColor: c.lineStrong }}>
                    {on && <Icon name="check" size={12} color="#fff" />}
                  </View>
                  <AppIcon icon={a.icon} size={26} />
                  <Text style={{ fontWeight: FW.medium, fontSize: FS.md, color: c.ink, flex: 1 }}>{a.name}</Text>
                  <Tag tone="neutral">{a.category}</Tag>
                </Pressable>
              );
            })}
          </View>
          {err.apps && <Text style={{ color: c.danger, fontSize: FS.sm, marginTop: 6 }}>{err.apps}</Text>}
        </View>

        <View>
          <Text style={{ fontSize: FS.sm, fontWeight: FW.medium, color: c.ink2, marginBottom: 8 }}>Limits & quotas</Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12 }}>
            {LIMIT_FIELDS.map((l) => (
              <View key={l.key} style={{ width: '47%' }}>
                <PTextInput label={l.label} mono keyboardType="numeric" value={String(form.limits[l.key])} onChangeText={(v) => setLimit(l.key, +v || 0)} />
              </View>
            ))}
          </View>
        </View>

        <View style={{ flexDirection: 'row', gap: 12, alignItems: 'flex-end' }}>
          <View style={{ flex: 1 }}>
            <PSelect label="Visibility" value={form.visibility} onChange={(v) => set('visibility', v)}
              options={[{ value: 'public', label: 'Public — anyone can pick' }, { value: 'private', label: 'Private — invite only' }]} />
          </View>
          <View style={{ flex: 1, paddingBottom: 8 }}>
            <PSwitch checked={form.popular} onChange={(v) => set('popular', v)} label="Mark as popular" />
          </View>
        </View>
      </View>
    </SideDrawer>
  );
};

const AdminPlans: React.FC = () => {
  const { theme } = useTheme();
  const c = theme.colors;
  const { state, setState, isPlatformAdmin } = usePlatform();
  const toast = useToast();
  const [editor, setEditor] = useState<{ open: boolean; plan: Plan | null }>({ open: false, plan: null });
  const [confirm, setConfirm] = useState<Plan | null>(null);
  if (!isPlatformAdmin) return <PermissionDenied />;

  const remove = (p: Plan) => { setState((s) => ({ ...s, adminPlans: s.adminPlans.filter((x) => x.id !== p.id) })); toast.success(`${p.name} plan deleted.`); setConfirm(null); };
  const togglePublish = (p: Plan) => {
    setState((s) => ({ ...s, adminPlans: s.adminPlans.map((x) => (x.id === p.id ? { ...x, status: x.status === 'published' ? 'draft' : 'published' } : x)) }));
    toast.success(p.status === 'published' ? `${p.name} unpublished.` : `${p.name} published.`);
  };

  return (
    <ScrollView contentContainerStyle={{ padding: 28, maxWidth: 1100, width: '100%', alignSelf: 'center' }}>
      <SettingsHeader title="Plans" sub="Pricing tiers that bundle applications and set usage limits."
        action={<PButton kind="accent" icon="plus" onPress={() => setEditor({ open: true, plan: null })}>Create plan</PButton>} />

      <Panel bodyStyle={{ padding: 0 }}>
        <DataTable<Plan>
          minWidth={900}
          keyExtractor={(p) => p.id}
          rows={state.adminPlans}
          columns={[
            {
              key: 'name', header: 'Plan', width: 160,
              render: (p) => (
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                  <Text style={{ fontWeight: FW.semibold, color: c.ink }}>{p.name}</Text>
                  {p.popular && <Tag tone="accent">Popular</Tag>}
                </View>
              ),
            },
            { key: 'price', header: 'Price', width: 110, render: (p) => <Text style={{ fontFamily: theme.font.mono, color: c.ink }}>{p.price === 0 ? 'Free' : inr(p.price)}<Text style={{ color: c.ink4, fontSize: FS.xs }}>/{p.cycle === 'annual' ? 'yr' : 'mo'}</Text></Text> },
            {
              key: 'apps', header: 'Applications', width: 160,
              render: (p) => (
                <View style={{ flexDirection: 'row', gap: 4, alignItems: 'center' }}>
                  {p.apps.slice(0, 4).map((id) => { const a = state.applications.find((x) => x.id === id); return a ? <AppIcon key={id} icon={a.icon} size={24} /> : null; })}
                  {p.apps.length > 4 && <Text style={{ fontSize: FS.sm, color: c.ink3 }}>+{p.apps.length - 4}</Text>}
                </View>
              ),
            },
            { key: 'seats', header: 'Seats', width: 70, render: (p) => <Text style={{ fontFamily: theme.font.mono, color: c.ink }}>{p.limits.seats}</Text> },
            { key: 'vis', header: 'Visibility', width: 100, render: (p) => <Tag tone={p.visibility === 'public' ? 'neutral' : 'info'}>{p.visibility}</Tag> },
            { key: 'status', header: 'Status', width: 100, render: (p) => <StatusBadge status={p.status} /> },
            {
              key: 'actions', header: 'Actions', align: 'right',
              render: (p) => (
                <View style={{ flexDirection: 'row', gap: 6, justifyContent: 'flex-end' }}>
                  <PButton size="sm" kind="ghost" onPress={() => togglePublish(p)}>{p.status === 'published' ? 'Unpublish' : 'Publish'}</PButton>
                  <PButton size="sm" kind="ghost" icon="edit" iconOnly onPress={() => setEditor({ open: true, plan: p })} />
                  <PButton size="sm" kind="ghost" icon="trash" iconOnly onPress={() => setConfirm(p)} />
                </View>
              ),
            },
          ]}
        />
      </Panel>

      <PlanEditor open={editor.open} plan={editor.plan} onClose={() => setEditor({ open: false, plan: null })} />
      <ConfirmModal open={!!confirm} onClose={() => setConfirm(null)} title={`Delete ${confirm?.name} plan?`} confirmLabel="Delete plan"
        body={`Tenants currently on ${confirm?.name} keep it until they change plans, but no new tenants can select it. This can't be undone.`}
        onConfirm={() => confirm && remove(confirm)} />
    </ScrollView>
  );
};

export default AdminPlans;
